/**
 * Unified Driver Card Component
 * 
 * Replaces DriverCard, EnhancedDriverCard, and UnifiedDriverCard
 * Reduces codebase by ~900 lines while maintaining all functionality
 * 
 * Variants:
 * - 'basic': Simple driver info display
 * - 'detailed': Full driver info with task progress
 * - 'enhanced': Drag-and-drop enabled for dispatch
 * - 'compact': Space-efficient for lists
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useDroppable, useDraggable } from '@dnd-kit/core'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import {
  ChevronDown,
  ChevronUp,
  Phone,
  CheckCircle,
  Circle,
  AlertCircle,
  Truck,
  ArrowRight,
  ArrowLeft,
  ArrowLeftRight,
  Package,
  MessageCircle,
  ClipboardList,
  CalendarClock,
  MapPin,
  Clock,
  CalendarCheck,
  X,
  Zap
} from 'lucide-react'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ContainerTask } from '../../types/container'
import { generateRouteNodes } from '../../utils/routeGenerator'
import { useBobtailQuote } from '../../hooks/useBobtailQuote'
import {
  apptDayKey,
  apptDayLabelShort,
  compareDayKeys,
  comparePlannedTasks,
  dayTone,
  dropSpentDays,
  isTaskSettled,
  plannedDayKey,
  NO_APPT_KEY,
} from '../../utils/apptSchedule'
import { Assignment } from '../../pages/dispatch/types/dispatch'
import type { YardCreditPool } from '../../services/driverYardCreditApi'
import { yardColor, UNKNOWN_YARD_LABEL } from '../../utils/yardCreditColors'
import { DraggableAssignedTask } from '../../pages/dispatch/components/DraggableAssignedTask'
import { AssignedTaskCard } from './AssignedTaskCard'
import { 
  getStatusStyles,
  getDriverStatusBadge, 
  getTaskBorderColor 
} from '../../utils/statusUtils'

/**
 * 一天一个排序作用域（迁移 192）。
 *
 * 只有派车台开了任务拖动才套 SortableContext —— 司机任务页、司机管理页拿的是
 * 同一个 DriverCard，但那两页没有 DndContext，套上去就会在挂载时直接报错。
 */
const TaskSortScope: React.FC<{
  enabled: boolean
  ids: string[]
  children: React.ReactNode
}> = ({ enabled, ids, children }) => {
  if (!enabled) return <>{children}</>
  return (
    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
      {children}
    </SortableContext>
  )
}

// Haversine distance in miles (for dispatch bobtail preview)
function haversineDistanceMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const BOBTAIL_DISTANCE_THRESHOLD_MILES = 5

/**
 * 一程活真正的收车点 —— bobtail 要从这里量到下一程的起点。
 *
 * 不能直接用 destination：Live Load 是三站（提箱 → 仓库卸货 → 空箱还回码头），
 * destination 只是中间那站仓库，司机实际停在第三站。拿仓库去比，就会给「卸完
 * 就在同一个码头接下一趟」算出一段根本不存在的空驶，方向还是反的。
 *
 * 也不能一律改用 return location：普通 Drop 单上的还箱地点是**以后**另一趟活
 * 的事，这趟司机放下柜子就走了 —— 按它算同样会凭空多出一段。
 *
 * 所以只在一种情形下改口：卡片画出来的最后一站既不是 destination、又正好是这
 * 趟 Live Load 自己要去的还柜地点。其余一律还是 destination —— 包括「最后一站
 * 对不上任何已知地点」，那时候宁可用老答案，也不能拿起点去冒充收车点
 * （空箱还柜那种两站活，末尾节点的动作也叫 return，按动作名判就会翻车）。
 */
export function finalStopOf(task: ContainerTask): { zoneId?: number; lat?: number; lng?: number } {
  const destination = {
    zoneId: task.destinationZoneId,
    lat: task.destinationLat,
    lng: task.destinationLng,
  }

  const nodes = generateRouteNodes(task)
  const lastStop = nodes.length > 0 ? nodes[nodes.length - 1] : null
  if (!lastStop) return destination
  // 最后一站就是 destination —— 绝大多数任务走这条，维持原样。
  if (lastStop.type === task.destinationType && lastStop.id === task.destinationId) return destination

  // 只有 Live Load 的还柜是这趟自己跑的。别的任务型上挂的那个 return 地点说的是
  // 「这柜子以后要还到哪」——另开一趟活，不是这趟的收车点。
  const runsItsOwnReturn = task.taskSubtype === 'live-load'
  const endsAtReturnLocation = !!task.returnLocationType
    && lastStop.type === task.returnLocationType
    && lastStop.id != null
    && lastStop.id === task.returnLocationId

  if (runsItsOwnReturn && endsAtReturnLocation && task.returnLocationLat && task.returnLocationLng) {
    return {
      zoneId: task.returnLocationZoneId,
      lat: task.returnLocationLat,
      lng: task.returnLocationLng,
    }
  }
  return destination
}

/**
 * 两张任务卡之间那条「空驶」分隔线。
 *
 * 除了里程，还显示这一脚按工资费率卡该付司机多少钱 —— 派单的人当场就能判断
 * 这么排划不划算，不用回头去翻工资表。钱是服务端按「谁领这笔」算好的总数
 * （公司司机开老板的车是两笔，这里显示合计）。
 *
 * 三种状态各有各的说法，不能混：
 *   有价    → `BT ~41mi · $75`
 *   没配价  → `BT ~41mi · 未配费率`（红字）—— 司机这一脚现在是白跑的
 *   还没答复 → 只显示里程，不占位、不跳动
 */
function BobtailDivider({
  driverId, fromZoneId, toZoneId, miles, isPreview,
}: {
  driverId: number | null
  fromZoneId: number | null
  toZoneId: number | null
  miles?: number
  isPreview: boolean
}) {
  const quote = useBobtailQuote(driverId, fromZoneId, toZoneId)
  const rule = isPreview ? 'border-amber-300' : 'border-orange-300'
  const pill = isPreview
    ? 'bg-amber-50 text-amber-600 border border-amber-200'
    : 'bg-orange-100 text-orange-600'

  const unpriced = quote && quote.amount === null && quote.unpricedRoles.length > 0
  // 两笔里只配了一笔 —— 显示的钱是偏低的，得说一声，否则调度以为就这么多。
  const partlyPriced = !!quote && quote.amount !== null && quote.unpricedRoles.length > 0

  const title = [
    isPreview
      ? `Bobtail preview — cross-zone gap ~${miles} mi`
      : 'Bobtail — cross-zone deadhead to pickup',
    quote?.amount != null
      ? `Driver pay ${quote.breakdown.map(b => `${b.role === 'truck_owner' ? 'Truck owner' : 'Driver'} $${b.amount}`).join(' + ')}`
      : null,
    unpriced ? 'No bobtail rate for this leg — the driver is paid $0 for it under the current rate card' : null,
    partlyPriced
      ? `Missing rate for: ${quote!.unpricedRoles.map(u => (u.role === 'truck_owner' ? 'Truck owner' : 'Driver')).join(', ')}`
      : null,
  ].filter(Boolean).join('\n')

  return (
    <div className="flex items-center gap-1.5">
      <div className={`flex-1 border-t border-dashed ${rule}`}></div>
      <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${pill}`}
        title={title}
      >
        <Truck className="w-3 h-3" />
        BT{miles ? ` ~${miles}mi` : ''}
        {quote?.amount != null && (
          <>
            <span aria-hidden="true">·</span>
            <span className="tabular-nums">
              ${Number.isInteger(quote.amount) ? quote.amount : quote.amount.toFixed(2)}
              {partlyPriced ? '+' : ''}
            </span>
          </>
        )}
        {unpriced && (
          <>
            <span aria-hidden="true">·</span>
            <span className="text-red-600">No rate</span>
          </>
        )}
      </span>
      <div className={`flex-1 border-t border-dashed ${rule}`}></div>
    </div>
  )
}

// Core interfaces
interface Task {
  id: string
  name: string
  status: "pending" | "in-progress" | "completed"
  container?: ContainerTask
}

interface Driver {
  id: string
  name: string
  phone: string
  plateLicense: string
  company: string
  avatar: string
  tasks: Task[]
  status?: string
  driverType?: 'owner-operator' | 'company-driver' | 'owners-driver' | string
  truckNumber?: string
  /** City of the driver's last GPS fix, e.g. "Oakland, CA". Empty = never seen. */
  location?: string
  /** Age of that fix. Without it a city from three days ago reads as "now". */
  locationUpdatedAt?: string
  todayTasks?: number
  /** @deprecated No longer rendered — the stat cell it filled now shows the
   *  driver's current city. Kept so existing callers keep type-checking. */
  hoursOnDuty?: number
  /** Yard-credit pools, one per yard, already ordered by yard colour rank.
   *  `undefined` = host did not load the ledger (renders '--'); an empty array
   *  is a real "no credits anywhere" and renders as a muted zero. Pools are NOT
   *  summed — two yards at different distances are different currencies. */
  yardCredits?: YardCreditPool[]
  userId?: number
}

// Variant configurations
type DriverCardVariant = 'basic' | 'detailed' | 'enhanced' | 'compact'

interface DriverCardConfig {
  variant: DriverCardVariant
  showAvatar?: boolean
  showCompany?: boolean
  showTaskProgress?: boolean
  showTaskList?: boolean
  enableDropZone?: boolean
  enableDragFromTasks?: boolean
  enableSelection?: boolean
  defaultTaskListOpen?: boolean
  compactSpacing?: boolean
  maxTasksVisible?: number
  onTaskProgressClick?: () => void
  onYardCreditsClick?: () => void
  onDriverClick?: () => void
  onTaskClick?: (task: ContainerTask) => void
  /** Opt-in: renders a history button in the header when provided. */
  onHistoryClick?: () => void
  /**
   * Opt-in：在头上多一个「排这天的活」按钮，打开派车确认窗口，只装这位司机。
   *
   * 那个窗口原来只有「有新派的活」才弹得出来。可有些活根本不需要派人 ——
   * Live Load 今天卸完、明天才去还柜，任务早就在这位司机手上、正在跑，
   * 缺的只是还柜那一程的票。没有这个入口，那种柜子永远约不上。
   */
  onPlanClick?: () => void
  /** Opt-in: forwarded to DraggableAssignedTask, which renders the button. */
  onTaskDetailClick?: (task: ContainerTask) => void
  /**
   * Tasks the caller wants pointed out — currently the container-number hits
   * from the driver search. The card opens its task list for them and draws
   * them in a marked-out style, so the answer to "who has this container" is
   * the task itself rather than a collapsed card.
   */
  highlightedContainerIds?: Set<string>
}

// Variant presets
const VARIANT_CONFIGS: Record<DriverCardVariant, Partial<DriverCardConfig>> = {
  basic: {
    showAvatar: true,
    showCompany: true,
    showTaskProgress: false,
    showTaskList: false,
    enableDropZone: false,
    compactSpacing: false
  },
  detailed: {
    showAvatar: true,
    showCompany: true,
    showTaskProgress: true,
    showTaskList: true,
    enableDropZone: false,
    defaultTaskListOpen: true,
    compactSpacing: false
  },
  enhanced: {
    showAvatar: true,
    showCompany: true,
    showTaskProgress: true,
    showTaskList: true,
    defaultTaskListOpen: true,
    enableDropZone: true,
    enableDragFromTasks: true,
    enableSelection: true,
    compactSpacing: false
  },
  compact: {
    showAvatar: true,
    showCompany: false,
    showTaskProgress: false,
    showTaskList: false,
    compactSpacing: true,
    enableDropZone: false
  }
}

interface DriverCardProps {
  driver: Driver
  variant?: DriverCardVariant
  config?: Partial<DriverCardConfig>
  className?: string
  containerTasks?: ContainerTask[]
  assignedTasks?: Array<{
    assignment: Assignment
    container: ContainerTask
    isDraft?: boolean
    draftId?: string
  }>
  isSelected?: boolean
  isDropping?: boolean
  onRemoveDraft?: (draftId: string) => void
  /** Task numeric ID to auto-attach as task reference when navigating to messages */
  messageTaskId?: number
}

// Draggable FF-Task card for confirmed assignments in Driver Card
const DraggableFFTask: React.FC<{
  container: ContainerTask
  assignment: Assignment
  getPortAbbreviation: (port: string) => string
}> = ({ container, assignment, getPortAbbreviation }) => {
  const ffStatus = (container as any).ff_status || container.status
  const matchedCtr = (container as any).matched_container_number
  // Only non-matched FF-Tasks can be unassigned
  const canDrag = ffStatus !== 'matched'

  const elementRef = useRef<HTMLDivElement>(null)
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `assignment-${assignment.id}`,
    disabled: !canDrag,
    data: { assignment, container, type: 'assignment' },
  })
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    elementRef.current = node
    setNodeRef(node)
  }, [setNodeRef])

  const style = useMemo(() => {
    if (!transform) return isDragging ? { zIndex: 1001 } : undefined
    return {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      zIndex: 1001,
    }
  }, [transform, isDragging])

  return (
    <div
      ref={combinedRef}
      style={style}
      {...(canDrag ? listeners : {})}
      {...(canDrag ? attributes : {})}
      className={`bg-teal-50 rounded-lg border border-teal-200 overflow-hidden transition-shadow ${
        isDragging ? 'opacity-60 shadow-xl scale-[1.02] rotate-1 cursor-grabbing' : ''
      } ${canDrag ? 'cursor-grab hover:shadow-md active:cursor-grabbing' : ''}`}
    >
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-3 mb-1.5">
          <div className="flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-teal-500" />
            <span className="text-xs font-semibold text-slate-900">{container.taskId}</span>
          </div>
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
            ffStatus === 'matched' ? 'bg-green-100 text-green-700'
            : ffStatus === 'picking' ? 'bg-amber-100 text-amber-700'
            : 'bg-blue-100 text-blue-700'
          }`}>
            {ffStatus}
          </span>
        </div>
        <div className="flex items-center gap-2 bg-teal-50 rounded-md px-2.5 py-1.5 border border-teal-200">
          <span className="text-xs font-semibold text-purple-600">
            {getPortAbbreviation(container.portName || '')}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-teal-100 text-teal-700">
            Free-Flow
          </span>
          {matchedCtr && (
            <span className="ml-auto text-[10px] font-mono text-green-700">{matchedCtr}</span>
          )}
        </div>
      </div>
    </div>
  )
}

export const DriverCard = React.memo<DriverCardProps>(({
  driver,
  variant = 'detailed',
  config: customConfig = {},
  className = "",
  containerTasks = [],
  assignedTasks = [],
  isSelected = false,
  isDropping = false,
  onRemoveDraft,
  messageTaskId
}) => {
  // Merge variant config with custom config
  const config: DriverCardConfig = useMemo(() => ({
    variant,
    ...VARIANT_CONFIGS[variant],
    ...customConfig
  }), [variant, customConfig])

  /**
   * Task list open state.
   *
   * Two things can open the list: the dispatcher clicking the chevron, and a
   * search hitting one of this card's containers. They are kept as separate
   * inputs and combined on every render rather than written into one state —
   * an earlier version copied the pre-search state into a ref and restored it
   * on clear, which made the result depend on the order the clicks and the
   * search updates happened to land in.
   *
   * Derived this way the rules hold no matter the order: a search opens the
   * list, clearing the search drops straight back to whatever the dispatcher
   * had, and collapsing during a search is honoured instead of being fought.
   */
  const [manuallyOpen, setManuallyOpen] = useState(config.defaultTaskListOpen ?? false)
  const [collapsedDuringSearch, setCollapsedDuringSearch] = useState(false)
  const hasHighlight = (config.highlightedContainerIds?.size ?? 0) > 0
  const isTaskListOpen = manuallyOpen || (hasHighlight && !collapsedDuringSearch)

  const setIsTaskListOpen = useCallback((open: boolean) => {
    setManuallyOpen(open)
    // Collapsing while a search is pinning the list open only makes sense as
    // "stop pinning it" — otherwise the chevron would visibly do nothing.
    if (!open && hasHighlight) setCollapsedDuringSearch(true)
  }, [hasHighlight])

  // Each new search starts with a clean slate: an earlier dismissal must not
  // hide the next hit.
  useEffect(() => {
    if (!hasHighlight) setCollapsedDuringSearch(false)
  }, [hasHighlight])

  /**
   * 「今天跑完的活」默认收起，一天一个开关（key = 那一天的 dayKey）。
   *
   * 跑完的活就地定住在它跑的那一天，所以越到下午，卡上越是被已完成的大卡片撑满，
   * 还没跑的那几张要往下翻才看得见 —— 而调度员盯的恰恰是还没跑的。收起来只是
   * 不画那几张卡，日子、序号、手排位次一律不动（位次还是按整天存的，否则一收起
   * 再拖一次就会把跑完的那几张从顺序里抹掉）。
   */
  const [expandedCompletedDays, setExpandedCompletedDays] = useState<Set<string>>(() => new Set())
  const toggleCompletedDay = useCallback((dayKey: string) => {
    setExpandedCompletedDays(prev => {
      const next = new Set(prev)
      if (next.has(dayKey)) next.delete(dayKey)
      else next.add(dayKey)
      return next
    })
  }, [])

  // Drop zone setup (conditional)
  const { isOver, setNodeRef } = useDroppable({
    id: config.enableDropZone ? `driver-${driver.id}` : 'disabled',
    data: config.enableDropZone ? {
      driver,
      type: 'driver'
    } : undefined,
    disabled: !config.enableDropZone
  })

  // Memoized calculations - prioritize assignedTasks over containerTasks
  //
  // 整天都跑完了的过去日子在这一步就摘掉（`dropSpentDays`，派车确认窗口同一条
  // 规矩）。摘在这儿而不是摘在分日之后，是为了让卡顶那根进度条和 x/y 跟着一起
  // 变 —— 否则卡上列 4 条、条上写 5/8，等于把看不见的活算进了今天的账。
  const tasksToShow = useMemo(() => {
    const all = assignedTasks.length > 0
      ? assignedTasks.map(task => task.container)
      : (containerTasks.length > 0 ? containerTasks : [])
    return dropSpentDays(all)
  }, [assignedTasks, containerTasks, driver.name])

  const { completedTasks, totalTasks } = useMemo(() => {
    const completed = tasksToShow.length > 0 
      ? tasksToShow.filter(task => task?.status === "completed").length
      : (driver?.tasks || []).filter(task => task?.status === "completed").length
    const total = tasksToShow.length > 0 ? tasksToShow.length : (driver?.tasks || []).length
    return { completedTasks: completed, totalTasks: total }
  }, [tasksToShow, driver.tasks])

  // Utility functions - moved before usage
  // allDay: 迁移 174 —— 存的是当天 00:00，直接显示时间会变成 "12:00AM"
  const formatTimeOnly = useCallback((dateString: string, allDay?: boolean) => {
    if (!dateString || dateString === "Pending") return "Pending"
    if (allDay) return "All Day"
    try {
      const date = new Date(dateString)
      const hours = date.getHours()
      const minutes = date.getMinutes()
      const ampm = hours >= 12 ? 'PM' : 'AM'
      const displayHours = hours % 12 || 12
      return `${displayHours}:${minutes.toString().padStart(2, '0')}${ampm}`
    } catch {
      return "Invalid"
    }
  }, [])

  /**
   * Split the driver's tasks into day sections.
   *
   * Which day a task belongs to comes from the shared scheduling helpers — the
   * same ones the board's day separators and the confirmation modal use. The
   * local version this replaced read `portAppointment || warehouseAppointment`
   * straight off the row, which disagreed with the board on three counts: it
   * couldn't see appointments that only exist on generated route stops, it
   * treated a NAR stop as unbooked, and it filed every task with no appointment
   * at all under today. It also left the day sections in whatever order the
   * tasks happened to arrive in, so a later day could sit above an earlier one.
   */
  const groupTasksByDate = useCallback((tasks: ContainerTask[]) => {
    const now = new Date()
    const groups = new Map<string, ContainerTask[]>()

    for (const task of tasks) {
      // 分日和日内次序都走共用那一份（apptSchedule 的 plannedDayKey /
      // comparePlannedTasks）—— 派车确认窗口读的是同一个函数。
      // 这里曾经自己算过一份，于是同一张任务在窗口里排在 Mon Sep 21、
      // 回到这张卡上躺在 Today。
      const key = plannedDayKey(task, now)
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(task)
    }

    return Array.from(groups, ([key, list]) => ({
      key,
      dateString: apptDayLabelShort(key, now),
      // 过期那一天要标红，所以 now 必须传进去（dayTone 少了它一律按星期几上色）。
      // 以前这张卡把过期的活折进今天，永远生不出过去的 key，漏传也看不出来。
      //
      // 整段都收了尾就不标红：跑完的活现在留在它跑的那一天，那天没什么可催的。
      // 同一个过去的日子里要是还压着没跑完的活，红照旧 —— 那才是要追的。
      tone: dayTone(key, now, { settled: list.every(isTaskSettled) }),
      isToday: key === apptDayKey(now, now),
      tasks: [...list].sort((a, b) => comparePlannedTasks(a, b, key, now)),
    })).sort((a, b) => compareDayKeys(a.key, b.key))
  }, [])

  const taskGroups = useMemo(() => {
    if (!config.showTaskList) return null
    return groupTasksByDate(tasksToShow.slice(0, config.maxTasksVisible || 50))
  }, [tasksToShow, config.showTaskList, config.maxTasksVisible, groupTasksByDate])

  const getPortAbbreviation = useCallback((portName: string): string => {
    const portMap: { [key: string]: string } = {
      "SSA Marine - PCT": "SSA",
      "ETS Terminal": "ETS", 
      "TRAPAC": "TRP",
      "Maston Terminal": "MST",
      "CBP Terminal": "CBP"
    }
    return portMap[portName] || portName.substring(0, 3).toUpperCase()
  }, [])

  const getWarehouseAbbreviation = useCallback((warehouseName: string): string => {
    if (warehouseName.length <= 6) return warehouseName
    const words = warehouseName.split(' ')
    if (words.length >= 2) {
      return words.map(word => word.charAt(0)).join('').toUpperCase().substring(0, 4)
    }
    return warehouseName.substring(0, 6).toUpperCase()
  }, [])

  const getPortWarehouseDirection = useCallback((taskType: string) => {
    switch (taskType) {
      case 'load-out':
        return { icon: ArrowRight, text: 'Port → Warehouse' }
      case 'load-in':
        return { icon: ArrowLeft, text: 'Warehouse → Port' }
      case 'empty-out':
        return { icon: ArrowRight, text: 'Port → Warehouse' }
      case 'empty-in':
        return { icon: ArrowLeft, text: 'Warehouse → Port' }
      default:
        return { icon: ArrowLeftRight, text: 'Bidirectional' }
    }
  }, [])

  const getCardClasses = useCallback(() => {
    let classes = 'bg-white rounded-xl p-4 cursor-pointer transition-[box-shadow,border-color] duration-200 relative group border-2 '

    if (isSelected) {
      classes += 'border-cyan-500 bg-cyan-50/30 shadow-md '
    } else {
      classes += 'border-gray-100 shadow-sm hover:shadow-md hover:border-gray-300 '
    }

    if (config.enableDropZone && (isOver || isDropping)) {
      classes += 'shadow-lg '
    }

    return classes
  }, [isSelected, config.enableDropZone, isOver, isDropping])

  const getDropZoneStyles = useMemo(() => {
    if (!config.enableDropZone) return {}
    
    const baseStyle = "transition-[box-shadow,transform,background-color] duration-150"
    
    if (isOver || isDropping) {
      return {
        className: `${baseStyle} ring-2 ring-cyan-400 ring-offset-2 bg-cyan-50 scale-[1.02] shadow-lg`,
        style: {}
      }
    }
    
    return {
      className: baseStyle,
      style: {}
    }
  }, [config.enableDropZone, isOver, isDropping])

  const driverStatusBadge = useMemo(() => 
    getDriverStatusBadge(
      driver.status, 
      driver.tasks?.some(task => task.status === "in-progress") || false
    ),
    [driver.status, driver.tasks]
  )

  // Render task list
  const renderTaskList = useCallback(() => {
    if (taskGroups && taskGroups.length > 0) {
      // groupIndex 还留着 —— 空驶提示要往上一天的最后一张任务看。
      return taskGroups.map((group, groupIndex) => {

        // 这一天里能手排的任务（迁移 192）。
        //
        // 草稿和 FF 任务不算：草稿还没落库，FF 卡背后没有真正的 task 行，位次
        // 写不进去。它们仍然按时间待在原处，只是拖不动、也不会被拖动影响。
        //
        // 「还没归到哪一天」那一段整段不让排：位次是挂在某一天上的，这一段压根
        // 没有日子可挂。留着让人拖只会拖完弹回去 —— 看着像坏了。
        const sortableTasks = group.key === NO_APPT_KEY ? [] : group.tasks.filter(container => {
          if (!container) return false
          if (String(container.id).startsWith('ff-')) return false
          const found = assignedTasks.find(task => task.container.id === container.id)
          return !!found?.assignment && !found.isDraft
        })
        const sortContext = {
          driverId: driver.id,
          dayKey: group.key,
          taskIds: sortableTasks.map(container => String(container.id)),
        }
        // 跑完的那几张：默认收起，点一下才展开。
        // 搜柜号搜中的那张是个例外 —— 搜出来却看不见，等于没搜到。
        const completedInGroup = group.tasks.filter(task => task && task.status === 'completed')
        const completedExpanded = expandedCompletedDays.has(group.key)
          || completedInGroup.some(task => config.highlightedContainerIds?.has(task.id))
        const hideCompleted = completedInGroup.length > 0 && !completedExpanded

        // 收起来的那几张不画，也就不能留在 SortableContext 里（dnd-kit 会按 id
        // 去量一个不存在的盒子）。但上面的 sortContext.taskIds 必须仍是**整天**的
        // 顺序 —— 那是要落库的那一列，缺了谁谁的位次就没了。
        const sortableIds = sortableTasks
          .filter(container => !(hideCompleted && container.status === 'completed'))
          .map(container => {
            const found = assignedTasks.find(task => task.container.id === container.id)
            return `assignment-${found!.assignment.id}`
          })

        return (
          <div key={group.key}>
            {/* Every section is labelled, today's included — the point of the
                separator is telling the dispatcher which day a card belongs to,
                and an unlabelled first block reads as "undated", not "today". */}
            <div className="flex items-center gap-2 py-2">
              <div className={`flex-1 border-t border-dashed ${group.tone.rule}`}></div>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md border ${group.tone.pill}`}>
                {group.dateString}
              </span>
              <div className={`flex-1 border-t border-dashed ${group.tone.rule}`}></div>
            </div>

            <TaskSortScope enabled={!!config.enableDragFromTasks} ids={sortableIds}>
            <div className="space-y-3">
              {completedInGroup.length > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleCompletedDay(group.key)
                  }}
                  aria-expanded={completedExpanded}
                  className="w-full flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50 text-emerald-700 transition-colors"
                >
                  <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="text-[11px] font-semibold">
                    {completedInGroup.length} completed
                  </span>
                  <span className="ml-auto flex items-center gap-1 text-[10px] font-medium flex-shrink-0">
                    {completedExpanded ? 'Hide' : 'Show'}
                    {completedExpanded
                      ? <ChevronUp className="w-3.5 h-3.5" />
                      : <ChevronDown className="w-3.5 h-3.5" />}
                  </span>
                </button>
              )}
              {group.tasks.map((container, taskIndex) => {
                if (!container) return null
                // 收起状态下不画这张卡。序号照旧按整天数（taskIndex 不受影响），
                // 展开后看到的号跟派车确认窗口仍然逐字一致。
                if (hideCompleted && container.status === 'completed') return null

                // 序号**每一天各自从 1 开始**，和派车确认窗口逐字一致。
                // 以前只有「今天」那一段用跨天累计号，别的日子用组内号 ——
                // 今天恰好是第一段时两者相等，所以这个分叉一直没被看见。
                const taskNumber = taskIndex + 1
                const assignedTask = assignedTasks.find(task => task.container.id === container.id)
                const assignment = assignedTask?.assignment
                const direction = getPortWarehouseDirection(container.taskType)

                // Bobtail indicator — confirmed (driver started task with GPS)
                // OR dispatch preview (consecutive tasks cross-zone, distance > 5mi)
                let showBobtail = container.isBobtailLeg
                let bobtailDistanceMiles: number | undefined
                let bobtailIsPreview = false

                // Check previous task: same group or last task of previous group
                const prevTask = taskIndex > 0
                  ? group.tasks[taskIndex - 1]
                  : (groupIndex > 0 ? taskGroups[groupIndex - 1].tasks[taskGroups[groupIndex - 1].tasks.length - 1] : null)

                const prevFinalStop = prevTask ? finalStopOf(prevTask) : null

                if (!showBobtail && prevFinalStop
                  && prevFinalStop.zoneId && container.originZoneId
                  && prevFinalStop.zoneId !== container.originZoneId
                  && prevFinalStop.lat && prevFinalStop.lng
                  && container.originLat && container.originLng) {
                  const dist = haversineDistanceMiles(
                    prevFinalStop.lat, prevFinalStop.lng,
                    container.originLat, container.originLng
                  )
                  if (dist > BOBTAIL_DISTANCE_THRESHOLD_MILES) {
                    showBobtail = true
                    bobtailDistanceMiles = Math.round(dist)
                    bobtailIsPreview = true
                  }
                }

                const bobtailIndicator = showBobtail ? (
                  <BobtailDivider
                    driverId={Number(driver.id) || null}
                    // 已判定的那条用服务端记下的区域对（工资就是按它算的）；
                    // 预览那条还没进数据库，用这一屏推出来的两头。
                    fromZoneId={(bobtailIsPreview ? prevFinalStop?.zoneId : container.bobtailFromZoneId) ?? null}
                    toZoneId={(bobtailIsPreview ? container.originZoneId : container.bobtailToZoneId) ?? null}
                    miles={bobtailDistanceMiles}
                    isPreview={bobtailIsPreview}
                  />
                ) : null

                // Draft task — reads as a normal task card so a dispatcher checks
                // the same things in the same places whether the assignment is
                // pending or confirmed; only the amber dashed border and the
                // Draft badge (in place of the status pill) mark it as pending.
                //
                // FF drafts keep their own compact shape, because a *confirmed*
                // FF task renders as its own card too — matching the regular
                // card here would make the draft look unlike what it becomes.
                if (assignedTask?.isDraft && assignedTask?.draftId) {
                  const isFFTask = !!(container as any).is_free_flow
                  const removeDraftButton = onRemoveDraft ? (
                    <button
                      className={`p-0.5 rounded transition-colors ${
                        isFFTask
                          ? 'hover:bg-teal-200 text-teal-600 hover:text-teal-800'
                          : 'hover:bg-amber-200 text-amber-600 hover:text-amber-800'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation()
                        onRemoveDraft(assignedTask.draftId!)
                      }}
                      aria-label="Remove draft"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  ) : null

                  if (!isFFTask) {
                    return (
                      <React.Fragment key={container.id}>
                      {bobtailIndicator}
                      <AssignedTaskCard
                        container={container}
                        taskNumber={taskNumber}
                        getPortAbbreviation={getPortAbbreviation}
                        getWarehouseAbbreviation={getWarehouseAbbreviation}
                        // A regular draft points at a task that already exists,
                        // so the details button works here exactly as it does on
                        // a confirmed card. (FF drafts carry a synthetic id and
                        // never reach this branch.)
                        onTaskDetailClick={config.onTaskDetailClick}
                        headerClassName="bg-amber-100/60 border-amber-200"
                        routeRowClassName="bg-amber-100/60"
                        taskNumberClassName="bg-amber-200 text-amber-800"
                        // Bangs are needed to beat the card's own bg/border,
                        // which are equally specific.
                        className="!bg-amber-50 !border-2 !border-dashed !border-amber-400"
                        statusSlot={
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-200/70 text-amber-800">
                            <Clock className="w-3 h-3" aria-hidden="true" />
                            Draft
                          </span>
                        }
                        headerAction={removeDraftButton}
                      />
                      </React.Fragment>
                    )
                  }

                  return (
                    <React.Fragment key={container.id}>
                    {bobtailIndicator}
                    <div className="rounded-lg border-2 border-dashed overflow-hidden relative bg-teal-50 border-teal-400">
                      {/* Draft badge + remove button */}
                      <div className="flex items-center justify-between px-3 py-1.5 border-b bg-teal-100/60 border-teal-200">
                        <div className="flex items-center gap-1.5">
                          <Zap className="w-3 h-3 text-teal-600" />
                          <span className="text-[11px] font-semibold text-teal-700">FF Draft</span>
                        </div>
                        {removeDraftButton}
                      </div>
                      {/* Task body */}
                      <div className="px-3 py-2.5">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-1.5">
                            <Zap className="w-3.5 h-3.5 text-teal-500" />
                            <span className="text-xs font-mono font-semibold text-slate-900">
                              {container.containerNumber}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 bg-teal-50 rounded-md px-2.5 py-1.5 border border-teal-200">
                          <span className="text-xs font-semibold text-purple-600">
                            {getPortAbbreviation(container.portName || "")}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-teal-50 text-teal-700 border border-teal-200">
                            Free-Flow
                          </span>
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  )
                }

                // Confirmed FF-Task — teal draggable card
                if ((container as any).is_free_flow && !assignedTask?.isDraft && assignment) {
                  return (
                    <React.Fragment key={container.id}>
                    {bobtailIndicator}
                    <DraggableFFTask
                      container={container}
                      assignment={assignment}
                      getPortAbbreviation={getPortAbbreviation}
                    />
                    </React.Fragment>
                  )
                }

                // Use draggable version if enabled and assignment exists
                if (config.enableDragFromTasks && assignment) {
                  return (
                    <React.Fragment key={container.id}>
                    {bobtailIndicator}
                    <DraggableAssignedTask
                      container={container}
                      assignment={assignment}
                      taskNumber={taskNumber}
                      direction={direction}
                      getPortAbbreviation={getPortAbbreviation}
                      getWarehouseAbbreviation={getWarehouseAbbreviation}
                      formatTimeOnly={formatTimeOnly}
                        onTaskDetailClick={config.onTaskDetailClick}
                      isHighlighted={config.highlightedContainerIds?.has(container.id)}
                      sort={sortContext}
                    />
                    </React.Fragment>
                  )
                }
                
                // Special displays for OFF and STANDBY containers
                if (container.status === "off") {
                  return (
                    <div key={container.id} className="bg-slate-100 rounded-lg border border-slate-300 overflow-hidden">
                      <div className="flex items-center justify-center px-4 py-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-slate-700 mb-2">OFF</div>
                          <div className="text-sm text-slate-600">
                            {new Date(container.portAppointment).toLocaleDateString('en-US', { 
                              weekday: 'short', month: 'short', day: 'numeric' 
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }

                if (container.status === "standby") {
                  return (
                    <div key={container.id} className="bg-blue-100 rounded-lg border border-blue-300 overflow-hidden">
                      <div className="flex items-center justify-center px-4 py-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-700 mb-2">STANDBY</div>
                          <div className="text-sm text-blue-600">
                            {new Date(container.portAppointment).toLocaleDateString('en-US', { 
                              weekday: 'short', month: 'short', day: 'numeric' 
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }
                
                // Regular task display — same card as the dispatch board, minus
                // the drag affordances. Driver Tasks and Driver Management land
                // here, so a task reads identically on all three pages.
                return (
                  <React.Fragment key={container.id}>
                  {bobtailIndicator}
                  <AssignedTaskCard
                    container={container}
                    taskNumber={taskNumber}
                    getPortAbbreviation={getPortAbbreviation}
                    getWarehouseAbbreviation={getWarehouseAbbreviation}
                    onTaskDetailClick={config.onTaskDetailClick}
                    className={`hover:shadow-md ${config.onTaskClick ? 'cursor-pointer' : ''}`}
                    onClick={(e) => {
                      if (config.onTaskClick) {
                        e.stopPropagation()
                        config.onTaskClick(container)
                      }
                    }}
                  />
                  </React.Fragment>
                )
              })}
            </div>
            </TaskSortScope>
          </div>
        )
      })
    }

    // Fallback to original task display
    return driver?.tasks?.map((task) => {
      if (!task) return null
      const statusStyles = getStatusStyles(task.status)
      return (
        <div key={task.id} className="flex items-center gap-3 p-4 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
          <div className="flex-shrink-0">
            {task.status === "completed" ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : task.status === "in-progress" ? (
              <AlertCircle className="w-5 h-5 text-blue-500" />
            ) : (
              <Circle className="w-5 h-5 text-slate-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{task.name}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusStyles.bgColor} ${statusStyles.textColor}`}>
                {statusStyles.label}
              </span>
            </div>
          </div>
        </div>
      )
    })
  }, [taskGroups, assignedTasks, config, getPortWarehouseDirection, getPortAbbreviation, getWarehouseAbbreviation, formatTimeOnly, driver?.tasks, onRemoveDraft, expandedCompletedDays, toggleCompletedDay])

  // Avatar helper
  const getInitials = useCallback((name: string) => {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return name.substring(0, 2).toUpperCase()
  }, [])

  // Progress bar color based on completion ratio
  const progressBarColor = useMemo(() => {
    if (totalTasks === 0) return 'bg-slate-300'
    const ratio = completedTasks / totalTasks
    if (ratio >= 0.8) return 'bg-green-500'
    if (ratio >= 0.4) return 'bg-yellow-500'
    return 'bg-red-500'
  }, [completedTasks, totalTasks])

  const isOwnerOperator = driver.driverType === 'owner-operator'
  const isOwnersDriver = driver.driverType === 'owners-driver'

  /* Yard-credit stat cell. Only a host that actually loaded the ledger passes a
     number, so `undefined` means "unknown" ('--') and is kept distinct from a
     real zero balance. Clickable only where a host wired up the drill-down. */
  const onYardCreditsClick = config.onYardCreditsClick
  const yardCreditsCell = useMemo(() => {
    const pools = driver.yardCredits
    const known = pools != null
    // A pool that settled back to zero is not news — it would only crowd out the
    // yard that still owes somebody a run.
    const open = (pools || []).filter(p => p.balance !== 0)

    const body = (
      <>
        <Package className={`w-3.5 h-3.5 flex-shrink-0 ${open.length ? 'text-slate-500' : 'text-gray-400'}`} />
        <div className="text-left min-w-0">
          <p className="text-gray-500 text-[10px] leading-tight">Yard credits</p>
          {!known ? (
            <p className="font-semibold tabular-nums text-gray-400">--</p>
          ) : open.length === 0 ? (
            <p className="font-semibold tabular-nums text-gray-400">0</p>
          ) : (
            /* One figure per yard, never a sum: a +1 at Oakland and a −1 at
               Hayward is two open pools, not a settled zero. Colour carries which
               yard, so the short name can stay small. */
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0 leading-tight">
              {open.map(p => {
                const c = yardColor(p.colorIndex)
                return (
                  <span key={p.yardId ?? 'none'} className={`inline-flex items-center gap-1 ${c.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} aria-hidden="true" />
                    <span className="text-[10px] opacity-80">{p.yardName || UNKNOWN_YARD_LABEL}</span>
                    <span className="font-semibold tabular-nums">
                      {p.balance > 0 ? `+${p.balance}` : p.balance}
                    </span>
                  </span>
                )
              })}
            </span>
          )}
        </div>
      </>
    )

    if (!onYardCreditsClick) {
      return <div className="flex items-center gap-1.5">{body}</div>
    }
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onYardCreditsClick() }}
        className="flex items-center gap-1.5 -mx-1 px-1 rounded-md hover:bg-gray-200/70 transition-colors"
        title="View yard credit ledger"
        aria-label={`View yard credit ledger for ${driver.name}`}
      >
        {body}
      </button>
    )
  }, [driver.yardCredits, driver.name, onYardCreditsClick])

  /* Current-location stat cell — took over the slot that used to show "On Duty".
     On Duty was hours since the oldest still-running task last changed, which
     is a stopwatch on task edits, not on the driver's shift.

     The city here comes from the driver's last GPS fix, resolved server-side.
     A fix has an age, and a city with no age attached is the dangerous case:
     "Oakland" from Tuesday looks exactly like "Oakland" from two minutes ago.
     So anything older than the driver's app plausibly still reporting gets
     greyed out and carries its age; the exact time is always in the tooltip. */
  const currentLocationCell = useMemo(() => {
    const city = driver.location?.trim()
    const fixTime = driver.locationUpdatedAt ? new Date(driver.locationUpdatedAt) : null
    const ageMs = fixTime && !isNaN(fixTime.getTime()) ? Date.now() - fixTime.getTime() : null

    // 30 min: the app pushes every 10s while tracking, so this much silence
    // means it stopped reporting, not that the driver stopped moving.
    const isStale = ageMs == null || ageMs > 30 * 60 * 1000

    const age = (() => {
      if (ageMs == null) return null
      const mins = Math.floor(ageMs / 60000)
      if (mins < 1) return 'now'
      if (mins < 60) return `${mins}m`
      const hours = Math.floor(mins / 60)
      if (hours < 24) return `${hours}h`
      return `${Math.floor(hours / 24)}d`
    })()

    const title = city
      ? (fixTime ? `${city} — GPS fix ${fixTime.toLocaleString()}` : city)
      : 'No GPS fix from this driver yet'

    return (
      <div className="flex items-center gap-1.5 min-w-0" title={title}>
        <MapPin className={`w-3.5 h-3.5 flex-shrink-0 ${city && !isStale ? 'text-cyan-500' : 'text-gray-400'}`} />
        {/* No "Current Location" caption — the pin already says what this is,
            and the city reads better on its own line height. */}
        <p className={`font-semibold truncate min-w-0 ${city && !isStale ? 'text-gray-900' : 'text-gray-400'}`}>
          {city ? (isStale && age ? `${city} · ${age}` : city) : '--'}
        </p>
      </div>
    )
  }, [driver.location, driver.locationUpdatedAt])

  // Shared card content renderer
  const renderCardContent = () => (
    <div className={getCardClasses()} onClick={config.onDriverClick}>
      {/* Card Header — avatar + name + driver type badge + status badge */}
      <div className={`flex items-center justify-between ${variant === 'compact' ? '' : 'mb-3'}`}>
        <div className="flex items-center gap-2.5 min-w-0">
          {/* Circular avatar with initials fallback */}
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden bg-cyan-50 ring-2 ring-white shadow-sm">
              {driver.avatar && !driver.avatar.includes('placeholder') ? (
                <img src={driver.avatar} alt={driver.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm font-semibold text-cyan-700">{getInitials(driver.name)}</span>
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-slate-900 text-sm leading-tight truncate">{driver.name}</h3>
            {variant !== 'compact' && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <p className="text-xs text-slate-500 truncate">{driver.company || '--'}</p>
                {driver.driverType && (
                  <span className={`inline-flex items-center text-[10px] flex-shrink-0 ${
                    isOwnerOperator
                      ? 'text-indigo-500'
                      : isOwnersDriver
                        ? 'text-amber-600'
                        : 'text-slate-400'
                  }`}>
                    · {isOwnerOperator ? 'Owner Op.' : isOwnersDriver ? "Owner's" : 'Co. Driver'}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* 排这天的活 —— 同样只在宿主接了回调时才出现 */}
          {config.onPlanClick && (
            <button
              className="p-1 rounded-md hover:bg-gray-100 text-slate-400 hover:text-cyan-600 transition-colors"
              onClick={(e) => {
                e.stopPropagation()
                config.onPlanClick?.()
              }}
              title="Plan this day / Book"
              aria-label={`Plan and book for ${driver.name}`}
            >
              <CalendarClock className="w-3.5 h-3.5" />
            </button>
          )}
          {/* Task history — only rendered where a host wired up the callback */}
          {config.onHistoryClick && (
            <button
              className="p-1 rounded-md hover:bg-gray-100 text-slate-400 hover:text-cyan-600 transition-colors"
              onClick={(e) => {
                e.stopPropagation()
                config.onHistoryClick?.()
              }}
              title="View task history"
              aria-label={`View task history for ${driver.name}`}
            >
              <ClipboardList className="w-3.5 h-3.5" />
            </button>
          )}
          {variant === 'compact' && (
            <button
              className="p-1 rounded-md hover:bg-gray-100 text-slate-400 hover:text-cyan-600 transition-colors"
              onClick={(e) => {
                e.stopPropagation()
                if (driver.userId) {
                  const params = new URLSearchParams({ driverUserId: String(driver.userId) })
                  if (messageTaskId) params.set('taskId', String(messageTaskId))
                  window.open(`/dispatch-console/messages?${params.toString()}`, '_blank')
                }
              }}
              aria-label="Send message"
            >
              <MessageCircle className="w-3.5 h-3.5" />
            </button>
          )}
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${driverStatusBadge.className}`}>
            <div className={driverStatusBadge.indicator}></div>
            {driverStatusBadge.label}
          </div>
        </div>
      </div>

      {/* Data Zone — unified gray background block (hidden for compact) */}
      {variant !== 'compact' && (
      <div className="bg-gray-50 rounded-lg p-2.5">
        {/* Info Row — truck + plate | phone | location + message */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs mb-2.5">
          <div className="flex items-center gap-1.5">
            <Truck className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" />
            <span className="font-mono font-medium text-slate-700">
              {driver.truckNumber || driver.plateLicense || '--'}
              {driver.truckNumber && driver.plateLicense ? ` · ${driver.plateLicense}` : ''}
            </span>
          </div>
          <span className="text-slate-300 select-none">|</span>
          <div className="flex items-center gap-1.5">
            <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
            <span className="text-slate-600">{driver.phone || '--'}</span>
          </div>
          {/* The location chip that used to sit here showed the same value the
              Current Location stat cell now owns — and before that, raw PostGIS
              hex, since it was reading a geography column. Removed rather than
              duplicated. */}
          <button
            className="ml-auto p-1 rounded-md hover:bg-white text-slate-400 hover:text-cyan-600 transition-colors flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation()
              if (driver.userId) {
                const params = new URLSearchParams({ driverUserId: String(driver.userId) })
                if (messageTaskId) params.set('taskId', String(messageTaskId))
                window.open(`/dispatch-console/messages?${params.toString()}`, '_blank')
              }
            }}
            aria-label="Send message"
          >
            <MessageCircle className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 mb-2.5" />

        {/* Quick Stats Row */}
        <div className="grid grid-cols-3 gap-3 text-xs mb-2.5">
          <div className="flex items-center gap-1.5">
            <CalendarCheck className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" />
            <div>
              <p className="text-gray-500 text-[10px] leading-tight">Today</p>
              {/* Server-computed: tasks with an appointment today. Falling back to
                  the driver's TOTAL task count would print a number that has
                  nothing to do with today, so a missing value shows '--'. */}
              <p className="font-semibold text-gray-900 tabular-nums">{driver.todayTasks ?? '--'}</p>
            </div>
          </div>
          {currentLocationCell}
          {/* Yard credits took over the old Rating slot — Rating was a constant
              5.0 placeholder with no source behind it. Always rendered (a muted
              '0'/'--' rather than nothing) so every card keeps the same height;
              a card that grows and shrinks makes the list jump under the
              dispatcher's cursor. */}
          {yardCreditsCell}
        </div>

        {/* Task Progress */}
        {config.showTaskProgress && (
          <Collapsible open={isTaskListOpen} onOpenChange={setIsTaskListOpen}>
            <div className="border-t border-gray-200 pt-2.5">
              <div
                className="cursor-pointer"
                onClick={config.onTaskProgressClick}
              >
                {totalTasks > 0 ? (
                  <div className="flex items-center gap-2.5">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`${progressBarColor} h-2 rounded-full transition-all duration-500 ease-out`}
                        style={{ width: `${(completedTasks / totalTasks) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-gray-600 tabular-nums flex-shrink-0">{completedTasks}/{totalTasks}</span>
                    {config.showTaskList && (
                      <CollapsibleTrigger className="p-1 hover:bg-gray-200 rounded transition-colors -mr-0.5">
                        {isTaskListOpen ? <ChevronUp className="h-3.5 w-3.5 text-gray-500" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-500" />}
                      </CollapsibleTrigger>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">No tasks assigned</span>
                    {config.showTaskList && (
                      <CollapsibleTrigger className="p-1 hover:bg-gray-200 rounded transition-colors -mr-0.5">
                        {isTaskListOpen ? <ChevronUp className="h-3.5 w-3.5 text-gray-500" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-500" />}
                      </CollapsibleTrigger>
                    )}
                  </div>
                )}
              </div>

              {config.showTaskList && (
                <CollapsibleContent className="mt-2 space-y-2">
                  {renderTaskList()}
                </CollapsibleContent>
              )}
            </div>
          </Collapsible>
        )}
      </div>
      )}
    </div>
  )

  // Render with or without drop zone
  if (config.enableDropZone) {
    const dropZoneProps = getDropZoneStyles
    return (
      <div
        ref={setNodeRef}
        style={dropZoneProps.style}
        className={`${dropZoneProps.className} ${className} rounded-lg relative`}
      >
        {renderCardContent()}

        {/* Drop Zone Indicator */}
        {(isOver || isDropping) && (
          <div className="absolute inset-0 border-2 border-dashed border-cyan-400 rounded-lg bg-cyan-50/50 flex items-center justify-center pointer-events-none z-10">
            <div className="flex items-center gap-2 bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
              <Truck className="w-4 h-4" />
              Assign to {driver.name}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {renderCardContent()}
    </div>
  )
})

DriverCard.displayName = 'DriverCard'